import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Pagination,
  Stack,
  CircularProgress,
  Alert,
  Grid,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { useAuth } from "../../components/features/auth/AuthGuard";
import { ROUTES, PERMISSIONS } from "../../constants";
import deviceAttendanceLogsAPI from "../../services/api/deviceAttendanceLogs.api";

const statusOptions = [
  { value: "", label: "All" },
  { value: "applied", label: "Applied" },
  { value: "rejected_unknown_pin", label: "Unknown PIN" },
  { value: "rejected_duplicate", label: "Duplicate" },
  { value: "rejected_other", label: "Rejected (other)" },
];

const StatusChip = ({ status }) => {
  const map = {
    applied: { label: "Applied", color: "success" },
    rejected_unknown_pin: { label: "Unknown PIN", color: "error" },
    rejected_duplicate: { label: "Duplicate", color: "warning" },
    rejected_other: { label: "Rejected", color: "error" },
  };
  const c = map[status] || { label: status, color: "default" };
  return <Chip label={c.label} color={c.color} size="small" />;
};

const DeviceAttendanceLogsPage = () => {
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  // Form inputs (what user types)
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [deviceSn, setDeviceSn] = useState("");
  const [pin, setPin] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Applied filters (sent to API only when user clicks Search)
  const [applied, setApplied] = useState({
    date_from: undefined,
    date_to: undefined,
    device_sn: undefined,
    pin: undefined,
    status: undefined,
  });

  const queryParams = {
    page,
    limit,
    date_from: applied.date_from || undefined,
    date_to: applied.date_to || undefined,
    device_sn: applied.device_sn || undefined,
    pin: applied.pin || undefined,
    status: applied.status || undefined,
    sortBy: "received_at",
    sortOrder: "DESC",
  };

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["device-attendance-logs", queryParams],
    queryFn: () => deviceAttendanceLogsAPI.getDeviceAttendanceLogs(queryParams),
    keepPreviousData: true,
    staleTime: 60 * 1000,
  });

  const payload = response?.data;
  const logs = payload?.data || [];
  const pagination = payload?.pagination || {};
  const totalPages = pagination.totalPages || 1;

  const handleSearch = () => {
    setApplied({
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      device_sn: deviceSn.trim() || undefined,
      pin: pin.trim() || undefined,
      status: statusFilter || undefined,
    });
    setPage(1);
  };

  const handleReset = () => {
    setDateFrom("");
    setDateTo("");
    setDeviceSn("");
    setPin("");
    setStatusFilter("");
    setApplied({
      date_from: undefined,
      date_to: undefined,
      device_sn: undefined,
      pin: undefined,
      status: undefined,
    });
    setPage(1);
  };

  if (!hasPermission(PERMISSIONS.VIEW_DEVICE_ATTENDANCE_LOGS)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to view device attendance logs.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Device Attendance Logs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All attendance data sent from ZKTeco device (or bridge) to the server. Filter by date, device SN, PIN, or status.
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Device SN"
                placeholder="e.g. TRU7251200231"
                value={deviceSn}
                onChange={(e) => setDeviceSn(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="PIN"
                placeholder="Registration number"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statusOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset}>
                  Reset
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error?.response?.data?.message || "Failed to load device logs."}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Received at</TableCell>
                      <TableCell>Device SN</TableCell>
                      <TableCell>PIN</TableCell>
                      <TableCell>Device date/time</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Employee</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No device logs found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            {row.received_at
                              ? new Date(row.received_at).toLocaleString()
                              : "—"}
                          </TableCell>
                          <TableCell>{row.device_sn || "—"}</TableCell>
                          <TableCell>{row.pin || "—"}</TableCell>
                          <TableCell>{row.device_datetime || "—"}</TableCell>
                          <TableCell>
                            {row.verify_state === 0 ? "Clock-in" : row.verify_state === 1 ? "Clock-out" : row.verify_state}
                          </TableCell>
                          <TableCell>
                            <StatusChip status={row.status} />
                          </TableCell>
                          <TableCell>
                            {row.user
                              ? `${row.user.first_name || ""} ${row.user.sur_name || ""}`.trim() || row.user.email
                              : "—"}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200 }} title={row.details}>
                            {row.details ? String(row.details).slice(0, 50) + (row.details.length > 50 ? "…" : "") : "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    color="primary"
                  />
                </Stack>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Showing {logs.length} of {pagination.total ?? 0} records
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DeviceAttendanceLogsPage;
